import { onMounted, onBeforeUnmount, readonly, shallowRef, watch } from "vue";

interface ShortcutBinding {
	/** The key held with Cmd/Ctrl, as `event.key` reports it. */
	key: string;
	/** i18n key naming what it does. */
	labelKey: string;
}

interface ShortcutOptions {
	/** Passing one opts the binding into the help popover. */
	labelKey?: string;
	/** Read reactively. While false the shortcut neither fires nor appears in the help. */
	enabled?: () => boolean;
}

// Labelled bindings add themselves here, so the help popover lists what the mounted view
// actually bound. shallowRef because `unlist` removes by identity, which a deep ref would
// break: it hands back proxies, never the raw object that went in.
const bindings = shallowRef<ShortcutBinding[]>([]);

/** May hold one chord twice while a route change has both views mounted — dedupe to display. */
export const boundShortcuts = readonly(bindings);

/** Letters first, so the list reads the same however the bindings happened to mount. */
function byKey(a: ShortcutBinding, b: ShortcutBinding): number {
	const isLetter = (s: string) => /^[a-z]$/i.test(s);
	if (isLetter(a.key) !== isLetter(b.key)) return isLetter(a.key) ? -1 : 1;
	return a.key.localeCompare(b.key);
}

/**
 * Lists a chord in the help popover without binding it, for a key handled elsewhere (⌘F, by
 * `usePageSearchShortcut`). Returns the function that takes it off the list again.
 */
export function listShortcutHelp(key: string, labelKey: string): () => void {
	const entry: ShortcutBinding = { key, labelKey };
	bindings.value = [...bindings.value, entry].sort(byKey);
	return () => {
		bindings.value = bindings.value.filter((b) => b !== entry);
	};
}

// While typing, the native shortcut must win (Cmd+Z = text undo, not model undo).
function isEditableTarget(target: EventTarget | null): boolean {
	return (
		target instanceof HTMLInputElement ||
		target instanceof HTMLTextAreaElement ||
		(target instanceof HTMLElement && target.isContentEditable)
	);
}

/** Binds Cmd/Ctrl + `key` for as long as the caller is mounted. */
export function useShortcut(key: string, handler: () => void, options: ShortcutOptions = {}) {
	const { labelKey, enabled } = options;

	// For letter keys, Shift must not be held — Cmd+Shift+Z (redo) is not Cmd+Z.
	// Non-letter keys keep Shift neutral: on some layouts "+" itself needs Shift.
	const isLetterKey = /^[a-z]$/i.test(key);

	function onKeydown(event: KeyboardEvent) {
		const mod = event.metaKey || event.ctrlKey;
		if (!mod || event.key !== key) return;
		if (isLetterKey && event.shiftKey) return;
		if (isEditableTarget(event.target)) return;
		// Before preventDefault, so an unavailable chord keeps its browser binding.
		if (enabled && !enabled()) return;
		event.preventDefault();
		handler();
	}

	// Its own entry, not a shared one: two views overlap during a route change and both
	// bind ⌘B, so the first to unmount must not delist the other's.
	let delist: (() => void) | null = null;

	function list() {
		if (!labelKey || delist) return;
		delist = listShortcutHelp(key, labelKey);
	}

	function unlist() {
		delist?.();
		delist = null;
	}

	if (labelKey) {
		watch(
			() => !enabled || enabled(),
			(available) => (available ? list() : unlist()),
			{
				immediate: true,
			},
		);
	}

	onMounted(() => window.addEventListener("keydown", onKeydown));

	onBeforeUnmount(() => {
		window.removeEventListener("keydown", onKeydown);
		unlist();
	});
}
